import axios from 'axios';
import {getAuth} from 'firebase/auth'
import { jwtDecode } from "jwt-decode";

axios.interceptors.request.use(
  async (config) => {
      let accessToken = localStorage.getItem('accessToken');
      const refreshThresholdSeconds = 120;
      if (!accessToken || jwtDecode(accessToken).exp < (Date.now() / 1000) - refreshThresholdSeconds) {
        accessToken = null;
        const user = getAuth().currentUser;
        if (user) {
          accessToken = await user.getIdToken(true);
          localStorage.setItem('accessToken', accessToken);
        }
      }
      if (accessToken) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
      }
      return config;
  },
  (error) => {
      return Promise.reject(error);
  }
);

// Pulls the specific message the backend sent (see app.py's error handlers
// and each endpoint's own jsonify({'message': ...}) responses) instead of
// the generic fallback every caller used to show regardless of whether the
// request 404'd, 400'd, or the network just dropped.
export const getErrorMessage = (error, fallback) => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.request && !error?.response) {
    return 'Unable to reach the server. Check your connection and try again.';
  }
  return fallback;
};

const BACKEND_API = process.env.REACT_APP_URI
  ? `${process.env.REACT_APP_URI}/api`
  : '/api';
const ACTIVITY_GRAPH_CACHE_DURATION_MS = 20 * 60 * 1000;
const activityGraphCache = new Map();
const ACTIVITIES_CACHE_DURATION_MS = 5 * 60 * 1000;
const activitiesCache = new Map();

// Requests in flight, keyed by method+url+params. Lets concurrent callers for
// the *same* request (e.g. React StrictMode's double effect invocation in
// dev, or a component re-rendering before the first response lands) share a
// single outbound network call instead of firing one each.
const inFlightRequests = new Map();
const dedupedGet = (url, params) => {
  const key = `${url}?${new URLSearchParams(params).toString()}`;
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key);
  }
  const request = axios.get(url, { params }).finally(() => {
    inFlightRequests.delete(key);
  });
  inFlightRequests.set(key, request);
  return request;
};

export const getProgress = (numActivities = 0, refreshStreams = false) =>
  dedupedGet(`${BACKEND_API}/progress`, {
    num_activities: numActivities,
    refresh_streams: refreshStreams,
  });

export const getActivityGraph = ({ startDate, endDate, grouping, metrics, activityTypes, aggregation }) => {
  const params = {
    start_date: startDate,
    end_date: endDate,
    grouping,
    metrics: metrics.join(','),
    activity_types: activityTypes.join(','),
    aggregation,
  };
  const cacheKey = new URLSearchParams(params).toString();
  const cachedResponse = activityGraphCache.get(cacheKey);
  if (cachedResponse && Date.now() - cachedResponse.createdAt < ACTIVITY_GRAPH_CACHE_DURATION_MS) {
    return Promise.resolve({ data: cachedResponse.data });
  }

  return dedupedGet(`${BACKEND_API}/activity-graph`, params).then((response) => {
    activityGraphCache.set(cacheKey, { data: response.data, createdAt: Date.now() });
    return response;
  });
};

// `date` (YYYY-MM-DD) asks the backend for the page that starts at that
// day rather than at the newest activity - the offset it resolves to comes
// back on the response, so paging on from there is plain offset paging.
export const getActivities = ({ limit = 20, offset = 0, date = null } = {}) => {
  const params = date ? { limit, date } : { limit, offset };
  const cacheKey = new URLSearchParams(params).toString();
  const cachedResponse = activitiesCache.get(cacheKey);
  if (cachedResponse && Date.now() - cachedResponse.createdAt < ACTIVITIES_CACHE_DURATION_MS) {
    return Promise.resolve({ data: cachedResponse.data });
  }

  return dedupedGet(`${BACKEND_API}/activities`, params).then((response) => {
    activitiesCache.set(cacheKey, { data: response.data, createdAt: Date.now() });
    return response;
  });
};

// Clears cached activity pages, e.g. after a Strava sync pulls in new activities.
export const invalidateActivitiesCache = () => {
  activitiesCache.clear();
};

// Clears cached activity-graph series, e.g. after a source disconnect
// deletes the activities those series were built from.
export const invalidateActivityGraphCache = () => {
  activityGraphCache.clear();
};

export const getActivity = (activityId) =>
  dedupedGet(`${BACKEND_API}/activities/${activityId}`, {});

export const registerStrava = (code) => {
  const stravaAuthUrl = `${BACKEND_API}/strava/client`;
  return axios.post(stravaAuthUrl, {
      code
    }
  );
}

export const getStravaConnection = () =>
  axios.get(`${BACKEND_API}/strava/client`).then(res => res.data);

// Removes the Strava connection and every activity it imported - the
// backend scopes the deletion to source_type STRAVA only, so activities
// from any other connected source are left alone.
export const disconnectStrava = () =>
  axios.delete(`${BACKEND_API}/strava/client`).then(() => {
    invalidateActivitiesCache();
    invalidateActivityGraphCache();
    // The Goals overview reads its totals from this cached snapshot before
    // any network round-trip (see AuthContent.js), and otherwise wouldn't
    // refresh it for up to another minute - clearing it forces the next
    // load to recompute from what's actually left in the database.
    localStorage.removeItem('progress');
    // Otherwise a still-active "importing in the background" banner (see
    // AuthContentWrapper.js) could keep showing for a connection that no
    // longer exists.
    localStorage.removeItem('stravaImportStartedAt');
  });

export const getGoals = () =>
  axios.get(`${BACKEND_API}/goals`);

export const createGoal = (goal) =>
  axios.post(`${BACKEND_API}/goals/goal`, goal);
  
export const updateGoal = (goal) => {
  console.log(goal);
  return axios.put(`${BACKEND_API}/goals/${goal.goalId}/goal`, goal);
}

export const deleteGoal = (goalId) =>
  axios.delete(`${BACKEND_API}/goals/${goalId}/goal`);

export const getUserSettings = () =>
  axios.get(`${BACKEND_API}/user/settings`).then(res => res.data);

export const setUserSettings = (distance_unit, elevation_unit) =>
  axios.post(`${BACKEND_API}/user/settings`, { distance_unit, elevation_unit }).then(res => res.data);

export const getYearPlan = (year) =>
  dedupedGet(`${BACKEND_API}/plan/year/${year}`, {});

export const getWeekPlan = (weekStartDate) =>
  dedupedGet(`${BACKEND_API}/plan/week/${weekStartDate}`, {});

export const updateWeekPlan = (weekStartDate, fields) =>
  axios.put(`${BACKEND_API}/plan/week/${weekStartDate}`, fields);

export const updateDayPlan = (date, fields) =>
  axios.put(`${BACKEND_API}/plan/day/${date}`, fields);

// Empties the planned value on every day of the week. The week's own
// planned value is untouched, as are event/notes/diary and the day session
// details. Responds with the week plan as it now stands - the days empty,
// since only loading the week page splits a week-level value back across
// them - or 409 if the week is no longer one that can be re-planned.
export const clearWeekDayGoalValues = (weekStartDate) =>
  axios.delete(`${BACKEND_API}/plan/week/${weekStartDate}/goals`);

export const updateWeekGoalValue = (weekStartDate, goalId, plannedValue) =>
  axios.put(`${BACKEND_API}/plan/week/${weekStartDate}/goal/${goalId}`, { plannedValue });

export const updateDayGoalValue = (date, goalId, plannedValue) =>
  axios.put(`${BACKEND_API}/plan/day/${date}/goal/${goalId}`, { plannedValue });